import cx from 'clsx';
import type { ReactNode } from 'react';
import {
  Button,
  Collection,
  Header,
  ListBox,
  ListBoxItem,
  ListBoxSection,
  Popover,
  Select,
  type SelectProps,
  SelectValue,
} from 'react-aria-components';
import { FormattedMessage } from 'react-intl';
import { IconChevronBottomOutlined } from '@repo/monochrome-icons';
import styles from './dropdown-selector.module.css';

interface DropdownItem {
  key: string;
  /**
   * @deprecated Do not pass translation keys via labelId,
   * pass the formatted message via label instead
   */
  labelId?: string;
  label?: string;
  optionLabel?: ReactNode;
  /**
   * @deprecated Do not pass translation keys via dropdownId
   */
  dropdownId?: string;
}

interface DropdownSection {
  key: string;
  dropdownId: string;
  items: DropdownItem[];
}

export type DropdownItems = DropdownItem[] | DropdownSection[];

interface DropdownSelectorProps extends Omit<SelectProps<DropdownItem>, 'onSelectionChange'> {
  items: DropdownItems;
  onSelectionChange: (key: string) => void;
  ariaLabel?: string;
  listBoxClassName?: string;
  listItemClassName?: string;
  popoverTriggerClassName?: string;
  buttonSize?: 'sm' | 'md';
}

function hasGroupedItems(items: DropdownItems): items is DropdownSection[] {
  return items.every(item => 'items' in item);
}

export function DropdownSelector({
  items,
  onSelectionChange,
  className,
  listBoxClassName,
  listItemClassName,
  popoverTriggerClassName,
  buttonSize = 'md',
  ...props
}: DropdownSelectorProps): ReactNode {
  // react-aria modal escapes the test container, we render it where it can find the modal (i.e. <div> component with 'ember-testing' id)
  const TEST_PORTAL_CONTAINER = document.getElementById('ember-testing') ?? undefined;

  return (
    <Select
      className={cx(styles.select, className)}
      data-testid="dropdown-selector"
      onSelectionChange={key => {
        // oxlint-disable-next-line typescript/consistent-type-assertions
        onSelectionChange(key as string);
      }}
      {...props}
    >
      <Button
        className={cx(
          buttonSize === 'md' && 'body-prominent',
          buttonSize === 'sm' && 'body-standard',
          styles.popoverTrigger,
          popoverTriggerClassName
        )}
        data-testid="trigger"
      >
        <SelectValue<DropdownItem>>
          {({ defaultChildren, selectedItem }) => {
            /** the whole render function can be removed once we stop passing labelId */
            if (selectedItem?.optionLabel) {
              return selectedItem.label;
            }
            if (selectedItem?.labelId) {
              // oxlint-disable-next-line @repo/no-dynamic-intl-id
              return <FormattedMessage id={selectedItem.labelId} />;
            }
            return defaultChildren;
          }}
        </SelectValue>
        <IconChevronBottomOutlined
          aria-hidden="true"
          className={cx(
            styles.arrowIcon,
            buttonSize === 'md' && styles.arrowIconMd,
            buttonSize === 'sm' && styles.arrowIconSm
          )}
        />
      </Button>
      <Popover UNSTABLE_portalContainer={TEST_PORTAL_CONTAINER}>
        {hasGroupedItems(items) ? (
          <ListBox
            className={cx(styles.listBox, listBoxClassName)}
            data-testid="dropdown-options"
            items={items}
          >
            {(section: DropdownSection) => (
              <ListBoxSection data-testid={section.dropdownId} id={section.key} key={section.key}>
                <Header className={styles.sectionHeader}>
                  {/* oxlint-disable-next-line @repo/no-dynamic-intl-id */}
                  <FormattedMessage id={section.dropdownId} />
                </Header>
                <Collection items={section.items}>
                  {(item: DropdownItem) => (
                    <ListBoxItem
                      className={cx(styles.listItem, listItemClassName)}
                      data-testid={item.key}
                      key={item.key}
                    >
                      {/* oxlint-disable-next-line @repo/no-dynamic-intl-id */}
                      {item.optionLabel ?? item.label ?? <FormattedMessage id={item.dropdownId} />}
                    </ListBoxItem>
                  )}
                </Collection>
              </ListBoxSection>
            )}
          </ListBox>
        ) : (
          <ListBox
            className={cx(styles.listBox, listBoxClassName)}
            data-testid="dropdown-options"
            items={items}
          >
            {item => (
              <ListBoxItem
                className={cx(styles.listItem, listItemClassName)}
                data-testid={item.key}
                key={item.key}
              >
                {/* oxlint-disable-next-line @repo/no-dynamic-intl-id */}
                {item.optionLabel ?? item.label ?? <FormattedMessage id={item.dropdownId} />}
              </ListBoxItem>
            )}
          </ListBox>
        )}
      </Popover>
    </Select>
  );
}
